import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { topicClusters } from '../data/topicClusters';
import { blogPosts } from '../data/blogData';
import { ArrowRight, Clock } from 'lucide-react';

export default function TopicHubPage() {
  const { cluster } = useParams();
  const [scrolled, setScrolled] = useState(false);
  const data = topicClusters.find((c) => c.slug === cluster);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    window.scrollTo(0, 0);
    return () => window.removeEventListener('scroll', onScroll);
  }, [cluster]);

  if (!data) return <Navigate to="/blog" replace />;
  const posts = data.postSlugs.map((s) => blogPosts.find((p) => p.slug === s)).filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${data.name} — Guides & Insights | Harpalyk Blog`}</title>
        <meta name="description" content={data.description} />
        <link rel="canonical" href={`https://harpalyk.com/blog/topics/${data.slug}`} />
      </Helmet>
      <Header scrolled={scrolled} />
      <main className="pt-16 lg:pt-[72px]" data-testid={`topic-hub-${data.slug}`}>
        <Breadcrumbs items={[{ label: 'Blog', href: '/blog' }, { label: data.name }]} />
        <section className="py-14 lg:py-20 px-6 lg:px-12 bg-gradient-to-b from-[#F8F9FA] to-white">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.25em] text-emerald-600 uppercase mb-4">Topic Cluster</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-gray-900 mb-5">{data.name}</h1>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">{data.description}</p>
            <Link to={data.link} className="inline-flex items-center gap-2 rounded-full bg-gray-900 text-white px-6 py-3 text-sm font-medium hover:bg-gray-700 transition-colors duration-300">
              Explore Related Products <ArrowRight size={15} />
            </Link>
          </div>
        </section>
        <section className="py-12 lg:py-16 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="group rounded-3xl overflow-hidden border border-black/5 bg-white hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform] duration-500 flex flex-col">
                <div className="h-44 overflow-hidden">
                  <img src={post.image} alt={post.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <span className="text-xs text-gray-500 flex items-center gap-1 mb-2"><Clock size={12} /> {post.readTime}</span>
                  <h2 className="font-heading text-base font-semibold text-gray-900 leading-snug group-hover:text-emerald-700 transition-colors duration-300">{post.title}</h2>
                </div>
              </Link>
            ))}
          </div>
          <div className="max-w-7xl mx-auto mt-12 text-sm text-gray-500">
            More topics:{' '}
            {topicClusters.filter((c) => c.slug !== data.slug).map((c, i, arr) => (
              <span key={c.slug}>
                <Link to={`/blog/topics/${c.slug}`} className="text-emerald-700 hover:underline">{c.name}</Link>
                {i < arr.length - 1 ? ' · ' : ''}
              </span>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
